import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { PlaygroundType, type PlaygroundConfig, type Resource, type ExposureType } from "@/services/api/widget/types";
import { WidgetAPI } from "@/services/api";
import type { DataPoint } from "@/components/chart/types";
import { isAccessDeniedError } from "@/utils/errorUtils";

// Define the new API response format
interface ColumnDefinition {
  id: string;
  label: string;
  type: string;
}

interface TimeseriesApiResponse {
  cols: ColumnDefinition[];
  rows: Array<[number, ...number[]]>; // First element is date timestamp, rest are values
  statistics?: Record<
    string,
    {
      total_return: number;
      volatility: number;
      sharpe_ratio: number;
      max_drawdown: number;
    }
  >;
  metadata: {
    display_mode: string;
    base_value: number;
    include_statistics: boolean;
    frequency: string;
    time_range: {
      start_date: string;
      end_date: string;
      preset: string | null;
    };
  };
}

// Define the scatter chart response structure
interface ScatterChartResponse {
  cols: Array<{
    id: string;
    label: string;
    type: string;
  }>;
  rows: Array<[string, string, number, number]>; // [id, name, x, y]
  metadata?: {
    x_label: string;
    y_label: string;
  };
}

// Define the context value shape
interface PlaygroundContextValue {
  config: PlaygroundConfig;
  setPlaygroundType: (type: PlaygroundType) => void;
  data: DataPoint[] | null; // null = never loaded, [] = loaded but empty
  assetStats: Record<string, any>;
  isLoading: boolean;
  error: Error | null;
  /** Resource IDs whose access was denied; these are filtered out of fetches. */
  accessDeniedIds: Set<string>;
  fetchData: (
    resources: Resource[],
    options?: {
      frequency?: string;
      statistic?: string;
      windowSize?: number;
      timeRange?: {
        preset?: string;
        start_date?: string;
        end_date?: string;
      };
      displayMode?: "index" | "performance";
      rebaseValue?: number;
      exposureType?: ExposureType;
      combineVariants?: boolean;
      exposureDisplayMode?: "weights" | "scaled_weights" | "values";
      xStatistic?: string;
      yStatistic?: string;
      xLabel?: string;
      yLabel?: string;
      includeLabels?: boolean;
      metricFieldPath?: string;
      extendIncompleteWithProxy?: boolean;
    }
  ) => Promise<void>;
}

// Create the context
const PlaygroundContext = createContext<PlaygroundContextValue | undefined>(
  undefined
);

// Provider props
interface PlaygroundProviderProps {
  children: React.ReactNode;
  initialConfig?: Partial<PlaygroundConfig>;
}

// Default configuration
const defaultConfig: PlaygroundConfig = {
  type: PlaygroundType.PerformanceHistory,
};

export const PlaygroundProvider: React.FC<PlaygroundProviderProps> = ({
  children,
  initialConfig = {},
}) => {
  // Combine default config with initial config
  const [config, setConfig] = useState<PlaygroundConfig>({
    ...defaultConfig,
    ...initialConfig,
  });

  // State for API data
  // null = never loaded, [] = loaded but empty, [...] = loaded with data
  const [apiData, setApiData] = useState<DataPoint[] | null>(null);
  const [apiStats, setApiStats] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [accessDeniedIds, setAccessDeniedIds] = useState<Set<string>>(new Set());

  // Set playground type
  const setPlaygroundType = useCallback((type: PlaygroundType) => {
    setConfig((prev) => ({ ...prev, type }));
  }, []);

  // Fetch data with custom parameters
  const fetchInProgressRef = useRef<boolean>(false);

  const fetchData = useCallback(
    async (
      allResources: Resource[],
      options?: {
        frequency?: string;
        statistic?: string;
        windowSize?: number;
        timeRange?: {
          preset?: string;
          start_date?: string;
          end_date?: string;
        };
        displayMode?: "index" | "performance";
        rebaseValue?: number;
        exposureType?: ExposureType;
        combineVariants?: boolean;
        exposureDisplayMode?: "weights" | "scaled_weights" | "values";
        xStatistic?: string;
        yStatistic?: string;
        xLabel?: string;
        yLabel?: string;
        includeLabels?: boolean;
        metricFieldPath?: string;
      }
    ) => {
      if (fetchInProgressRef.current) {
        return;
      }

      // Filter out resources for which access was previously denied.
      // When accessDeniedIds changes the fetchData reference is recreated, which
      // propagates through usePlaygroundData and re-triggers the fetch effect so
      // the chart automatically retries with only the allowed resources.
      const resources = allResources.filter(r => !accessDeniedIds.has(r.resource_id));

      if (!resources || !resources.length) {
        setApiData([]);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        // Reset data and stats to ensure loading indicator shows instead of stale data
        setApiData(null);
        setApiStats({});

        const frequency = (options?.frequency || "monthly").toLowerCase() as
          | "daily"
          | "weekly"
          | "monthly"
          | "quarterly"
          | "yearly";

        if (config.type === PlaygroundType.Exposure || config.type === PlaygroundType.ExposureColumns) {
          try {
            const response = (await WidgetAPI.getExposures({
              resources,
              exposure_type: options?.exposureType || "asset_allocation",
              as_at:
                options?.timeRange?.end_date ||
                new Date().toISOString().split("T")[0],
            })) as any; // Use 'any' temporarily to avoid type errors

            // Validate response structure - the API returns a flat object with resource IDs as keys
            if (!response) {
              throw new Error("Empty exposure response");
            }

            // For exposure data, don't transform it into DataPoints - pass the raw response to the adapter
            // This allows the adapter to decide between pie chart and stacked bar charts
            setApiData([response]);

            // Store the full exposure data in the stats for the adapter to use
            const exposureStats: Record<string, any> = {};

            Object.keys(response)
              .filter((key) => key !== "metadata" && !key.startsWith("_"))
              .forEach((resourceId) => {
                // Check if entry has rows property to confirm it's resource data
                const resourceData = response[resourceId];

                if (
                  resourceData &&
                  "rows" in resourceData &&
                  Array.isArray(resourceData.rows)
                ) {
                  exposureStats[resourceId] = {
                    exposures: resourceData.rows.map((row: any) => {
                      if (Array.isArray(row) && row.length >= 4) {
                        return {
                          name: String(row[0]),
                          value: Number(row[3]), // Use net value
                          long: Number(row[1]),
                          short: Number(row[2]),
                        };
                      } else {
                        return {
                          name: "Unknown",
                          value: 0,
                          long: 0,
                          short: 0,
                        };
                      }
                    }),
                  };
                }
              });

            setApiStats(exposureStats);
          } catch (error) {
            setError(
              error instanceof Error
                ? error
                : new Error("Failed to fetch exposure data")
            );
            // Reset data on error
            setApiData([]);
            setApiStats({});
          }
        } else if (config.type === PlaygroundType.ExposureOverTime) {
          if (resources.length !== 1) {
            setApiData([]);
            setApiStats({});
            return;
          }

          try {
            const response = await WidgetAPI.getExposureTimeseries({
              resources,
              exposure_type: options?.exposureType || "asset_allocation",
              time_range: options?.timeRange || { preset: "3Y" },
              frequency:
                options?.frequency === "monthly" ? "monthly" : "quarterly",
              display_mode: options?.exposureDisplayMode || "scaled_weights",
            });

            const cols = response.data.cols;
            const rows = response.data.rows;

            if (!cols || !rows || !Array.isArray(cols) || !Array.isArray(rows)) {
              throw new Error("Invalid exposure timeseries response format");
            }

            const transformedData: DataPoint[] = rows.map((row) => {
              const dataPoint: DataPoint = {
                date: new Date(row[0] as number),
              };

              cols.forEach((column, index) => {
                if (index > 0 && column.id) {
                  const value = row[index];
                  if (value === null || value === undefined) {
                    dataPoint[column.id] = null as unknown as number;
                  } else if (typeof value === "number") {
                    dataPoint[column.id] = value;
                  }
                }
              });

              return dataPoint;
            });

            setApiData(transformedData);
            setApiStats({ metadata: response.metadata });
          } catch (error) {
            setError(
              error instanceof Error
                ? error
                : new Error("Failed to fetch exposure timeseries data")
            );
            setApiData([]);
            setApiStats({});
          }
        } else if (config.type === PlaygroundType.PerformanceHistory) {
          if (!resources || resources.length === 0) {
            setApiData([]);
            return;
          }

          const response = (await WidgetAPI.getTimeseriesData({
            time_range: options?.timeRange || { preset: "5Y" },
            frequency,
            resources,
            display_mode: options?.displayMode || "index",
            base_value: options?.rebaseValue || 100,
            extend_incomplete_with_proxy: options?.extendIncompleteWithProxy ?? false,
          })) as unknown as TimeseriesApiResponse;

          const responseMetadata =
            (response as any).metadata ?? (response as any).data?.metadata;

          // Normalise response shape (supports both direct and wrapper `.data` format)
          const cols = (response as any).cols ?? (response as any).data?.cols;
          const rows = (response as any).rows ?? (response as any).data?.rows;

          if (!cols || !rows || !Array.isArray(cols) || !Array.isArray(rows)) {
            throw new Error("Invalid API response format");
          }

          // Transform API response to DataPoint format
          const transformedData: DataPoint[] = rows.map((row: any[]) => {
            const dataPoint: DataPoint = {
              // First column is always the date (as timestamp in milliseconds)
              date: new Date(row[0]),
            };

            // Add each resource value to the data point using column definitions
            cols.forEach((column: ColumnDefinition, index: number) => {
              // Skip the first column which is the date
              if (index > 0 && column.id && typeof row[index] === "number") {
                // IMPORTANT: Store using the EXACT column.id as the primary key
                // This ensures consistency with the statistics object keys
                dataPoint[column.id] = row[index] as number;

                // For convenience, also store under simplified formats
                const parts = column.id.split(":");
                const simplifiedId = parts.pop() || column.id;
                if (simplifiedId !== column.id) {
                  dataPoint[simplifiedId] = row[index] as number;
                }

                // Store under last part of ResourceType.PRODUCT format
                if (column.id.includes("ResourceType.PRODUCT")) {
                  const productParts = column.id.split(".");
                  if (productParts.length > 1) {
                    const resourceType = productParts[1].split(":")[0]; // Extract PRODUCT part
                    if (resourceType && resourceType !== column.id) {
                      dataPoint[resourceType] = row[index] as number;
                    }
                  }
                }
              }
            });

            return dataPoint;
          });

          // Extract resource IDs from column definitions - use EXACT column IDs as primary identifiers
          const resourceIds = cols
            .slice(1) // Skip the first column which is the date
            .map((col: ColumnDefinition) => col.id)
            .filter((id: string) => id); // Filter out any undefined/null IDs

          // Extract and transform statistics from the API response
          const transformedStats: Record<string, any> = {};

          // Check if statistics are available in the response
          const statsObj =
            (response as any).statistics ?? (response as any).data?.statistics;
          if (statsObj) {
            // Handle both array and object formats
            const statsEntries = Array.isArray(statsObj)
              ? statsObj
              : Object.entries(statsObj).map(([assetId, stats]) => ({
                  id: assetId,
                  ...(stats as any),
                }));

            statsEntries.forEach((stat) => {
              // Create stats entry with standardized format
              const statEntry = {
                totalReturn: stat.total_return * 100, // Convert to percentage
                volatility: stat.volatility * 100, // Convert to percentage
                sharpeRatio: stat.sharpe_ratio,
                maxDrawdown: stat.max_drawdown * 100, // Convert to percentage
              };

              // Store under the exact ID
              transformedStats[stat.id] = statEntry;

              // Also store under alternate formats for flexible access
              const parts = stat.id.split(":");
              const simplifiedAssetId = parts.pop() || stat.id;
              if (simplifiedAssetId !== stat.id) {
                transformedStats[simplifiedAssetId] = statEntry;
              }
            });
          }

          // Set the statistics alongside the data
          setApiData(transformedData);
          setApiStats({
            ...transformedStats,
            metadata: responseMetadata,
          });
        } else if (
          config.type === PlaygroundType.ScatterChart
        ) {
          try {
            const response = (await WidgetAPI.getScatterChart({
              resources,
              x_statistic: options?.xStatistic || "generic-stat-annualized-3y",
              y_statistic:
                options?.yStatistic || "generic-stat-volatility-36m-m",
              x_label: options?.xLabel || "3y Annualised Performance",
              y_label: options?.yLabel || "3y Annualised Volatility",
              include_labels: true,
            })) as ScatterChartResponse;

            // The response structure is { rows, cols }
            const { cols, rows } = response;
            if (!rows || !Array.isArray(rows) || rows.length === 0) {
              throw new Error("Invalid or empty response data");
            }

            // Create a single DataPoint (the scatter chart doesn't use time series)
            const dataPoint: DataPoint = {
              date: new Date(), // Required field for DataPoint interface
            };

            // Extract labels from metadata
            const xLabel = cols[2]?.label || "X Value";
            const yLabel = cols[3]?.label || "Y Value";

            // Process each row as a scatter chart point [id, name, x, y]
            const transformedStats: Record<string, any> = {};

            rows.forEach((row) => {
              // Each row is [id, name, x, y]
              const resourceId = String(row[0]);
              // Remove everything preceding and including the colon if it exists
              const cleanedResourceId = resourceId.replace(/^.*:/, "");
              const resourceName = String(row[1]);
              const xValue = Number(row[2]);
              const yValue = Number(row[3]);

              // Store x value directly in dataPoint using resourceId as key
              dataPoint[cleanedResourceId] = xValue;

              // Store y value with _y suffix to differentiate from x value
              dataPoint[`${cleanedResourceId}_y`] = yValue;

              // Store complete point data in stats for easy access
              transformedStats[cleanedResourceId] = {
                name: resourceName,
                x: xValue,
                y: yValue,
                xLabel,
                yLabel,
                originalId: resourceId, // Keep the original ID for reference
              };
            });

            // Set the data in state
            setApiData([dataPoint]);
            setApiStats(transformedStats);
          } catch (error) {
            setError(
              error instanceof Error
                ? error
                : new Error("Failed to fetch scatter chart data")
            );
            setApiData([]);
            setApiStats({});
          }
        } else if (config.type === PlaygroundType.MetricsOverTime) {
          if (!resources || resources.length === 0) {
            setApiData([]);
            return;
          }

          const fieldPath =
            options?.metricFieldPath ||
            "calculated_fields.statistics.saltus-stat-volatility-3y-m-ex-ante.value";

          const response = (await WidgetAPI.getMetricTimeseries({
            resources,
            field_path: fieldPath,
            time_range: options?.timeRange || { preset: "3Y" },
            frequency: frequency as any,
          })) as unknown as TimeseriesApiResponse & { data?: TimeseriesApiResponse };

          const cols = response.cols ?? response.data?.cols;
          const rows = response.rows ?? response.data?.rows;

          if (!cols || !rows || !Array.isArray(cols) || !Array.isArray(rows)) {
            throw new Error("Invalid API response format");
          }

          const transformedData: DataPoint[] = rows.map((row) => {
            const dataPoint: DataPoint = {
              date: new Date(row[0]),
            };

            cols.forEach((column, index) => {
              if (index > 0 && column.id && typeof row[index] === "number") {
                dataPoint[column.id] = row[index] as number;
                const parts = column.id.split(":");
                const simplifiedId = parts.pop() || column.id;
                if (simplifiedId !== column.id) {
                  dataPoint[simplifiedId] = row[index] as number;
                }
              }
            });

            return dataPoint;
          });

          setApiData(transformedData);
          setApiStats({});
        } else if (
          config.type === PlaygroundType.RollingStats ||
          config.type === PlaygroundType.Underwater
        ) {
          // Rolling Stats (and Underwater which is a special case)
          const statistic = 
            config.type === PlaygroundType.Underwater 
              ? "underwater" 
              : (options?.statistic || "volatility");
          const windowSize = options?.windowSize || 12;

          // Backend now handles benchmark lookup per resource for beta/correlation
          const response = (await WidgetAPI.getRollingStats({
            resources,
            time_range: options?.timeRange || { preset: "5Y" },
            frequency,
            statistic: statistic as any,
            window_size: windowSize,
            window_frequency: frequency,
            extend_incomplete_with_proxy: options?.extendIncompleteWithProxy ?? false,
          })) as unknown as TimeseriesApiResponse;

          const rollingMetadata =
            (response as any).metadata ?? (response as any).data?.metadata;

          // Validate response structure before processing
          if (
            !response ||
            !response.cols ||
            !Array.isArray(response.cols) ||
            !response.rows ||
            !Array.isArray(response.rows)
          ) {
            throw new Error("Invalid API response format");
          }

          // Transform API response to DataPoint format
          const transformedData: DataPoint[] = response.rows.map((row) => {
            const dataPoint: DataPoint = {
              // First column is always the date (as timestamp in milliseconds)
              date: new Date(row[0]),
            };

            // Add each resource value to the data point using column definitions
            response.cols.forEach((column, index) => {
              // Skip the first column which is the date
              if (index > 0 && column.id && typeof row[index] === "number") {
                // IMPORTANT: Store using the EXACT column.id as the primary key
                // This ensures consistency with the statistics object keys
                dataPoint[column.id] = row[index] as number;

                // For convenience, also store under simplified formats
                const parts = column.id.split(":");
                const simplifiedId = parts.pop() || column.id;
                if (simplifiedId !== column.id) {
                  dataPoint[simplifiedId] = row[index] as number;
                }

                // Store under last part of ResourceType.PRODUCT format
                if (column.id.includes("ResourceType.PRODUCT")) {
                  const productParts = column.id.split(".");
                  if (productParts.length > 1) {
                    const resourceType = productParts[1].split(":")[0]; // Extract PRODUCT part
                    if (resourceType && resourceType !== column.id) {
                      dataPoint[resourceType] = row[index] as number;
                    }
                  }
                }
              }
            });

            return dataPoint;
          });

          // Extract and transform statistics from the API response
          const transformedStats: Record<string, any> = {};

          // Check if statistics are available in the response
          if (response.statistics) {
            // Handle both array and object formats
            const statsEntries = Array.isArray(response.statistics)
              ? response.statistics
              : Object.entries(response.statistics).map(([assetId, stats]) => ({
                  id: assetId,
                  ...stats,
                }));

            statsEntries.forEach((stat) => {
              // Create stats entry with standardized format
              const statEntry = {
                totalReturn: stat.total_return * 100, // Convert to percentage
                volatility: stat.volatility * 100, // Convert to percentage
                sharpeRatio: stat.sharpe_ratio,
                maxDrawdown: stat.max_drawdown * 100, // Convert to percentage
              };

              // Store under the exact ID
              transformedStats[stat.id] = statEntry;

              // Also store under alternate formats for flexible access
              const parts = stat.id.split(":");
              const simplifiedAssetId = parts.pop() || stat.id;
              if (simplifiedAssetId !== stat.id) {
                transformedStats[simplifiedAssetId] = statEntry;
              }
            });
          }

          // Set the statistics alongside the data
          setApiData(transformedData);
          setApiStats({
            ...transformedStats,
            metadata: rollingMetadata,
          });
        }
      } catch (err) {
        const denied = isAccessDeniedError(err);

        // isAccessDeniedError only populates resourceId when response.data
        // carries the structured RESOURCE_ACCESS_DENIED detail. Fall back to
        // directly reading the same path so we're resilient to edge cases where
        // the detail object is present but the utility doesn't extract it.
        const directResourceId =
          (err as any)?.response?.data?.detail?.resource_id as string | undefined;
        const resourceId = denied.resourceId || directResourceId;

        if (denied.isAccessDenied && resourceId) {
          // Mark resource as denied and let the auto-retry (triggered by state
          // change → new fetchData ref → useEffect re-fires) handle the re-fetch
          // without it. Don't show a global error banner.
          setAccessDeniedIds(prev => new Set([...prev, resourceId]));
        } else if (denied.isAccessDenied) {
          // 403 but no resource_id in the response — still suppress the error
          // banner: the chart will show no data while we have no specific target
          // to dim. The console log above will reveal the raw response shape.
          setApiData([]);
        } else {
          setError(
            err instanceof Error ? err : new Error("Failed to fetch data")
          );
          setApiData([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [config.type, accessDeniedIds]
  );

  // Create the context value
  const contextValue = useMemo(
    () => ({
      config,
      setPlaygroundType,
      data: apiData,
      assetStats: apiStats,
      isLoading,
      error,
      accessDeniedIds,
      fetchData,
    }),
    [config, setPlaygroundType, apiData, apiStats, isLoading, error, accessDeniedIds, fetchData]
  );

  return (
    <PlaygroundContext.Provider value={contextValue}>
      {children}
    </PlaygroundContext.Provider>
  );
};

// Hook to use the playground context
export const usePlayground = (): PlaygroundContextValue => {
  const context = useContext(PlaygroundContext);
  if (context === undefined) {
    throw new Error("usePlayground must be used within a PlaygroundProvider");
  }
  return context;
};

export default PlaygroundProvider;
